import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { AttachFileIcon } from 'components/common/Icon';
import { Routes } from 'fnRoutes';
import { Link } from 'react-router-dom';
import { ReactComponent as FilingNotReadyIcon } from 'icons/filing-not-ready.svg';
import { ReactComponent as FilingReadyIcon } from 'icons/filing-ready.svg';
import { ReadOnlyForm } from 'components/common/ReadOnlyForm';
import { Business } from 'models/business';
import { FormationsDivider } from 'components/common';
import { useDocumentsQuery } from 'hooks/api';
import { formatEIN } from 'helpers/progressTracker';
import { useTranslation } from 'react-i18next';
import { FormationsGhostButton, FormationsPrimaryButton } from 'components/common/Buttons';
import { ArrowBack } from '@mui/icons-material';
import { SubtitleText } from 'components/ProgressTracker/StyledTypography';

const IconContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

interface TParams {
  accountId: string;
  businessData: Business;
  completedSteps: number;
  stageCompleted: boolean;
  onContinueToAccountVerification: () => void;
  onBackClick: () => void;
}

export const FilingDocumentsBusinessInABox = ({
  accountId,
  businessData,
  completedSteps,
  stageCompleted,
  onContinueToAccountVerification,
  onBackClick,
}: TParams) => {

  const handleComplete = async () => {
    localStorage.setItem('filingDocumentsCompletedBIB', 'true');
    onContinueToAccountVerification();
  };

  const { t } = useTranslation();
  const { documents } = useDocumentsQuery({
    accountId,
    page: 1,
    category: ['Organizational Docs'],
    subcategory: ['Miscellaneous'],
  });

  if (completedSteps < 3) {
    return (
      <div>
        <Typography component="h5" variant="h5B">
          We need more info...
        </Typography>
        <SubtitleText variant="body1">
          {t('progressTracker.incorporation.completePreviousSteps')}
        </SubtitleText>
        <IconContainer>
          <FilingNotReadyIcon />
        </IconContainer>
      </div>
    );
  }
  if (!stageCompleted) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Typography
          component="h5"
          variant="h5B"
          data-test-id="got-your-information"
        >
          {t('progressTracker.incorporation.gotIncorporationInfo')}
        </Typography>
        <SubtitleText variant="body1">
          {t('progressTracker.incorporation.businessInABoxIncorporationSubtitle')}
        </SubtitleText>
        <IconContainer sx={{ marginTop: 0 }}>
          <FilingReadyIcon />
        </IconContainer>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 'auto',
          }}
        >
          <FormationsGhostButton
            size="large"
            startIcon={<ArrowBack />}
            onClick={onBackClick}
          >
            Back
          </FormationsGhostButton>
          <FormationsPrimaryButton
            onClick={handleComplete}
            data-testid="continue-to-account-verification"
          >
            Continue to Account Verification
          </FormationsPrimaryButton>
        </div>
      </div>
    );
  }
  return (
    <div>
      <Typography component="h5" variant="h5B">
        {t('progressTracker.incorporation.weHaveReceivedIncorporationInfo')}
      </Typography>
      <SubtitleText variant="body1">
        {t('progressTracker.incorporation.timeToCelebrate', {
          link: (
            <Link to={Routes.DOCUMENTS}>
              <Typography variant="body1B">Documents</Typography>
            </Link>
          ),
        })}
      </SubtitleText>
      <ReadOnlyForm
        items={[
          {
            key: 'Company Name (Legal)',
            title: 'Company Name (Legal)',
            value: businessData?.name,
          },
          {
            key: 'Date Of Incorporation',
            title: 'Date Of Incorporation',
            value: businessData?.incorporationDate,
          },
          {
            key: 'Employer Identification Number (EIN)',
            title: 'Employer Identification Number (EIN)',
            value: businessData?.ein
              ? formatEIN(businessData?.ein)
              : businessData?.ein,
          },
        ]}
      />
      {documents && documents?.length > 0 && (
        <Box
          sx={{
            mt: 2,
            mb: 4,
          }}
        >
          <FormationsDivider />
          <Typography component="h6" variant="h8B" sx={{ mb: 2 }}>
            {t(
              'progressTracker.accountVerification.documentOfIncorporation.title',
            )}
          </Typography>
          {documents?.map((item) => (
            <Typography
              key={item.id}
              variant="body1S"
              component="p"
              sx={{ mb: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AttachFileIcon sx={{ verticalAlign: 'text-bottom' }} />
                {item.title}
              </Box>
            </Typography>
          ))}
        </Box>
      )}
    </div>
  );
};
